// src/pages/InvoiceDetail.tsx — one invoice, as its own page (Finance → Invoices → a row, or New invoice).
//
// One component for /app/invoices/new, a draft (edited in place) and an issued invoice (read-only). The layout follows
// BillDetail: header with status and a ⋮ menu, then a tab strip: Summary (the invoice itself, edited in place while a
// draft), Details (its fields), Payments (record a receipt, and clear / fail the ones recorded) and Notes. ?tab= picks
// the tab, so the list's row menu can link straight to Payments.
//
// Prices are VAT-INCLUSIVE: the unit price typed (or taken from an item) is the gross, and the server backs the net
// out of it per line (invoices.service.ts computeLines). The Net / VAT / Total come from POST /invoices/preview, the
// same computation saving uses; while that is in flight a local estimate is shown.

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Plus, Trash2, X } from 'lucide-react';
import { api, invoiceNumberLabel, openInvoiceDocument } from '../lib/api';
import { useCan } from '../hooks/usePermission';
import { Card, Badge, Money, DateText, PageHeader, Button, Input, Select, Textarea, SearchSelect, ActionMenu, Tabs } from '../components/ui';
import { Confirm, FormError } from '../components/forms/common';
import { Can } from '../components/Can';
import { formatDate, formatMoneyExact } from '../lib/viz';
import type { CreateInvoiceBody, Invoice, InvoiceLine, InvoicePreview, InvoicingSettings, Item, TaxDefaults, TaxRate } from '../types/api';
import { PageLoader } from '../components/BrandLoader';

type InvoiceType = CreateInvoiceBody['invoiceType'];
type Entity = { id: string; name: string; region: 'UK' | 'AE'; functionalCurrency?: string | null };
type Tenancy = { id: string; reference: string; status?: string; propertyId?: string | null; unitId?: string | null; tenants?: Array<{ id: string; name: string }>; property?: { name: string }; unit?: { reference: string } };
/** A receipt against this invoice (GET /payments?invoiceId=). */
type Receipt = { id: string; reference: string | null; receivedOn: string | null; method: string | null; amountMinor: number; currency: string; status: string; externalReference: string | null };

/** The API's PAYMENT_METHODS (src/modules/finance/schemas.ts). */
const METHODS = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' }, { value: 'CARD', label: 'Card' }, { value: 'DIRECT_DEBIT', label: 'Direct debit' },
  { value: 'CHEQUE', label: 'Cheque' }, { value: 'CASH', label: 'Cash' }, { value: 'STRIPE', label: 'Stripe' },
] as const;
const methodLabel = (m: string | null | undefined) => METHODS.find((x) => x.value === m)?.label ?? pretty(m);

const TYPES: Array<{ value: InvoiceType; label: string }> = [
  { value: 'RENT', label: 'Rent' }, { value: 'SERVICE_CHARGE', label: 'Service charge' }, { value: 'RECHARGE', label: 'Recharge' },
  { value: 'DEPOSIT', label: 'Deposit' }, { value: 'SALE', label: 'Sale' },
];
const typeLabel = (t: string | null | undefined) => TYPES.find((x) => x.value === t)?.label ?? (t === 'CREDIT_NOTE' ? 'Credit note' : t ?? '');
const today = () => new Date().toISOString().slice(0, 10);
/** YYYY-MM-DD + n days (UTC, so no daylight-saving drift). */
const addDays = (iso: string, n: number) => (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(Date.parse(`${iso}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10) : iso);
const pct = (n: number) => `${String(Number(n.toFixed(2)))}%`;
const pretty = (s: string | null | undefined) => (s ? s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ') : '—');
const TH = 'px-3 py-1.5 text-[11.5px] font-medium uppercase tracking-wide text-ink-500';

/** One row per VAT rate in use, as a UK VAT invoice shows it. */
function vatSummary(lines: Array<Pick<InvoiceLine, 'taxCode' | 'taxRate' | 'netMinor' | 'taxMinor'>>) {
  const byRate = new Map<string, { code: string; rate: number; netMinor: number; taxMinor: number }>();
  for (const l of lines) {
    const k = `${l.taxCode}|${l.taxRate}`;
    const row = byRate.get(k) ?? { code: l.taxCode, rate: l.taxRate, netMinor: 0, taxMinor: 0 };
    row.netMinor += l.netMinor; row.taxMinor += l.taxMinor;
    byRate.set(k, row);
  }
  return [...byRate.values()].sort((a, b) => b.rate - a.rate || a.code.localeCompare(b.code));
}

const TABS = [['summary', 'Summary'], ['details', 'Details'], ['payments', 'Payments'], ['notes', 'Notes']] as const;
type Tab = (typeof TABS)[number][0];

/**
 * /app/invoices/new and /app/invoices/:id. A draft (with invoices.update) opens in edit mode; anything else is read-only.
 * ?tab=payments|details|notes opens that tab, ?void=1 opens Void (the list's row menu links here with them).
 */
export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const canJournals = useCan('journals.read');
  const canUpdate = useCan('invoices.update');
  const canCreate = useCan('invoices.create');
  const canPaymentsRead = useCan('payments.read');
  const canResidentsRead = useCan('residents.read');
  const canPayments = canPaymentsRead || canResidentsRead;
  const canRecordViaInvoice = useCan('invoices.recordPayment');
  const canPaymentsCreate = useCan('payments.create');
  const canRecord = canRecordViaInvoice || canPaymentsCreate;
  // The tab lives in the URL (?tab=), so it survives a reload and can be linked to.
  const tabParam = params.get('tab');
  const tab: Tab = !isNew && TABS.some(([k]) => k === tabParam) ? (tabParam as Tab) : 'summary';
  const setTab = (k: Tab) => {
    const next = new URLSearchParams(params);
    if (k === 'summary') next.delete('tab'); else next.set('tab', k);
    setParams(next, { replace: true });
  };
  // Bumped by every "Record payment": the Payments tab focuses its amount field.
  const [payFocus, setPayFocus] = useState(0);
  const goPay = () => { setTab('payments'); setPayFocus((n) => n + 1); };
  const [dialogState, setDialog] = useState<'void' | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [docError, setDocError] = useState<string | null>(null);
  useEffect(() => { if (!notice) return; const t = setTimeout(() => setNotice(null), 8000); return () => clearTimeout(t); }, [notice]);

  const { data: inv, isLoading, error } = useQuery({ queryKey: ['invoice', id], queryFn: async () => (await api.get<Invoice>(`/invoices/${id!}`)).data, enabled: !isNew });
  const receipts = useQuery({
    queryKey: ['/payments', { invoiceId: id }],
    queryFn: async () => (await api.get<Receipt[]>('/payments', { invoiceId: id, perPage: 100 })).data,
    enabled: !isNew && canPayments && !!inv && inv.status !== 'DRAFT',
    retry: false,
  });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['invoice', id] });
    void qc.invalidateQueries({ queryKey: ['/invoices'] });
    void qc.invalidateQueries({ queryKey: ['/payments'] });
  };

  // ?void=1 from the list's row menu opens Void; closing it drops the flag.
  const flag = params.get('void') === '1' ? 'void' : null;
  const closeDialog = () => {
    setDialog(null);
    if (flag) { const next = new URLSearchParams(params); next.delete('void'); setParams(next, { replace: true }); }
  };

  const issue = useMutation({ mutationFn: async () => api.post(`/invoices/${id!}/issue`, {}), onSuccess: () => { setNotice('Invoice issued and posted to the ledger.'); refresh(); } });
  const voidInv = useMutation({ mutationFn: async (reason: string) => api.post(`/invoices/${id!}/void`, { reason }), onSuccess: () => { closeDialog(); setNotice('Invoice voided.'); refresh(); } });
  const printDoc = () => { setDocError(null); openInvoiceDocument(id!).catch((e: Error) => setDocError(e.message)); };

  if (isNew && !canCreate) return <div className="py-12 text-center text-[12.5px] text-ink-400">You cannot create invoices.</div>;
  if (!isNew && error) return <div className="py-12 text-center text-[12.5px] text-red-600">{(error as Error).message}</div>;
  if (!isNew && (isLoading || !inv)) return <PageLoader />;

  const editing = isNew || (inv!.status === 'DRAFT' && canUpdate);
  const payable = !!inv && inv.outstandingMinor > 0 && inv.status !== 'DRAFT' && inv.status !== 'VOID';
  const voidable = !!inv && ['ISSUED', 'OVERDUE'].includes(inv.status) && inv.paidMinor === 0;
  const dialog = dialogState ?? (flag === 'void' && voidable ? 'void' : null);
  const journalLink = (jid: string | null, label: string) => jid && (canJournals
    ? <Link to={`/app/journals?open=${jid}`} className="text-bronze-600 hover:underline">{label}</Link>
    : <span>{label}</span>);

  return (
    <div className="mx-auto max-w-6xl space-y-2.5">
      <PageHeader back="/app/invoices" title={isNew ? 'New invoice' : inv!.customerName || 'No customer'}
        eyebrow={isNew ? 'Draft' : [invoiceNumberLabel(inv!.number), inv!.propertyName, inv!.unitReference, typeLabel(inv!.invoiceType), inv!.entityName].filter(Boolean).join(' · ')}
        actions={<>{inv?.isOverdue && <Badge value="OVERDUE" />}<Badge value={inv?.status ?? 'DRAFT'} /></>}
        menu={inv ? [
          inv.status === 'DRAFT' && { label: 'Issue', icon: 'Send', can: 'invoices.update', disabled: issue.isPending, onClick: () => issue.mutate(), title: 'Allocates the number and posts to the ledger' },
          payable && canRecord && { label: 'Record payment', icon: 'Banknote', onClick: goPay },
          { label: inv.status === 'DRAFT' ? 'Preview document' : 'Print / Save as PDF', icon: 'Printer', onClick: printDoc },
          voidable && { label: 'Void', icon: 'Ban', can: 'invoices.void', danger: true, onClick: () => setDialog('void') },
          !!inv.propertyId && { label: 'Open the property', icon: 'Building2', to: `/app/properties/${inv.propertyId}`, separator: true },
          !!inv.tenancyId && { label: `Open ${inv.tenancyReference ?? 'the tenancy'}`, icon: 'FileSignature', to: `/app/tenancies/${inv.tenancyId}`, separator: !inv.propertyId },
          !!inv.journalId && canJournals && { label: 'View journal', icon: 'BookOpen', to: `/app/journals?open=${inv.journalId}` },
        ] : []} />

      {notice && (
        <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-800">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /><span className="flex-1">{notice}</span>
          <button onClick={() => setNotice(null)} aria-label="Dismiss" className="rounded-md p-0.5 hover:bg-emerald-100"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {(issue.isError || docError) && <p className="rounded-lg bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-700">{issue.isError ? (issue.error as Error).message : docError}</p>}

      <Tabs items={TABS.map(([k, l]) => ({ key: k, label: isNew && k !== 'summary' ? <span className="cursor-not-allowed text-ink-300" title="Save the invoice first">{l}</span> : l }))}
        value={tab} onChange={(k) => { if (!(isNew && k !== 'summary')) setTab(k); }} />

      {tab === 'payments' && inv ? (
        <PaymentsTab inv={inv} payable={payable} canRecord={canRecord} canSee={canPayments} receipts={receipts.data} receiptsError={receipts.error as Error | null}
          focusToken={payFocus} onChanged={(msg) => { setNotice(msg); refresh(); }} />
      ) : tab === 'details' && inv ? (
        <DetailsTab key={inv.updatedAt ?? inv.id} inv={inv} canEdit={canUpdate} journalLink={journalLink} onSaved={(msg) => { setNotice(msg); refresh(); }} />
      ) : tab === 'notes' && inv ? (
        <NotesTab key={inv.updatedAt ?? inv.id} inv={inv} canEdit={canUpdate} onSaved={(msg) => { setNotice(msg); refresh(); }} />
      ) : editing ? (
        <InvoiceEditor key={inv?.updatedAt ?? 'new'} inv={inv ?? null}
          onCreated={(created) => { refresh(); navigate(`/app/invoices/${created.id}`, { replace: true }); }}
          onSaved={(msg) => { setNotice(msg); refresh(); }} />
      ) : (
        <InvoiceView inv={inv!} receipts={receipts.data} receiptsError={receipts.error as Error | null} canReceipts={canPayments}
          journalLink={journalLink} onPay={payable && canRecord ? goPay : undefined} />
      )}

      {inv && (
        <Confirm open={dialog === 'void'} onClose={closeDialog} title={`Void ${inv.status === 'DRAFT' ? 'this draft' : inv.number}?`}
          body="Reverses the AR journal in an open period. The number is kept; the invoice is marked VOID."
          confirmLabel="Void invoice" reasonLabel="Reason (required — goes on the audit trail)" pending={voidInv.isPending} error={voidInv.error} onConfirm={(r) => voidInv.mutate(r)} />
      )}
    </div>
  );
}

// ─── shared pieces ────────────────────────────────────────────────────────────

/** The meta card: details on the left, the balance on the right (as on the reference layout). */
function MetaCard({ rows, currency, balanceMinor, totalMinor, paidMinor, draft = false }: { rows: Array<{ label: string; value: ReactNode }>; currency: string; balanceMinor: number; totalMinor: number; paidMinor: number; draft?: boolean }) {
  return (
    <div className="surface grid sm:grid-cols-5">
      <dl className="space-y-2.5 px-5 py-4 text-[13px] sm:col-span-3">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex items-center gap-3">
            <dt className="w-28 shrink-0 text-ink-500">{label}</dt>
            <dd className="min-w-0 flex-1 text-ink-900">{value}</dd>
          </div>
        ))}
      </dl>
      <div className="flex flex-col justify-center border-t border-ink-100 bg-ink-50/60 px-5 py-4 sm:col-span-2 sm:border-l sm:border-t-0">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">Balance due</p>
        <Money exact minor={balanceMinor} currency={currency} className={`text-[20px] font-semibold tabular-nums ${balanceMinor > 0 || draft ? 'text-ink-900' : 'text-emerald-700'}`} />
        <div className="mt-3 grid grid-cols-2 gap-2 border-t border-ink-100 pt-3 text-[12px]">
          <div><p className="text-ink-500">Invoice total</p><Money exact minor={totalMinor} currency={currency} className="font-medium text-ink-800" /></div>
          <div><p className="text-ink-500">Paid</p><Money exact minor={paidMinor} currency={currency} className="font-medium text-ink-800" /></div>
        </div>
      </div>
    </div>
  );
}

function VatSummaryTable({ lines, currency }: { lines: Array<Pick<InvoiceLine, 'taxCode' | 'taxRate' | 'netMinor' | 'taxMinor'>>; currency: string }) {
  if (!lines.length) return null;
  return (
    <div className="mt-4 px-5">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">VAT summary by rate</p>
      <table className="w-full max-w-[360px] text-[13px]">
        <thead><tr className="border-b border-ink-100 text-left text-[11.5px] uppercase tracking-wide text-ink-500">
          <th className="py-1.5 font-medium">Rate</th><th className="py-1.5 text-right font-medium">Net</th><th className="py-1.5 text-right font-medium">VAT</th>
        </tr></thead>
        <tbody>{vatSummary(lines).map((r) => (
          <tr key={`${r.code}|${r.rate}`} className="border-b border-ink-50">
            <td className="py-1"><span className="font-mono text-[11.5px]">{r.code}</span> <span className="text-ink-500">{pct(r.rate)}</span></td>
            <td className="py-1 text-right"><Money exact minor={r.netMinor} currency={currency} /></td>
            <td className="py-1 text-right"><Money exact minor={r.taxMinor} currency={currency} /></td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function TotalRow({ label, minor, currency, strong, span }: { label: string; minor: number; currency: string; strong?: boolean; span: number }) {
  return (
    <tr className={strong ? 'font-semibold text-ink-900' : 'text-ink-500'}>
      <td className="px-3 pt-2 text-right" colSpan={span}>{label}</td>
      <td className="px-5 pt-2 text-right"><Money exact minor={minor} currency={currency} /></td>
    </tr>
  );
}

const IncVatChip = ({ on }: { on: boolean }) => (
  <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-500">{on ? 'Prices include VAT' : 'Prices are net of VAT'}</span>
);

// ─── view mode ────────────────────────────────────────────────────────────────

function InvoiceView({ inv, receipts, receiptsError, canReceipts, journalLink, onPay }: {
  inv: Invoice; receipts: Receipt[] | undefined; receiptsError: Error | null; canReceipts: boolean;
  journalLink: (jid: string | null, label: string) => ReactNode; onPay?: () => void;
}) {
  const c = inv.currency;
  const lines = inv.lines ?? [];
  const balance = inv.status === 'VOID' ? 0 : inv.outstandingMinor;
  // Newest first from the API; a failed receipt is not how the invoice was paid.
  const latest = receipts?.find((r) => r.status !== 'FAILED') ?? receipts?.[0];
  return (
    <div className="space-y-2.5">
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <MetaCard currency={c} balanceMinor={balance} totalMinor={inv.totalMinor} paidMinor={inv.paidMinor} rows={[
            { label: 'Billed to', value: <span className="font-semibold">{inv.customerName || '—'}</span> },
            { label: 'Invoice #', value: <span className="font-mono text-[12.5px]">{invoiceNumberLabel(inv.number)}</span> },
            { label: 'Issue date', value: <DateText value={inv.issueDate} /> },
            { label: 'Due date', value: <span className={inv.isOverdue ? 'font-medium text-red-600' : ''}><DateText value={inv.dueDate} /></span> },
            { label: 'Tenancy', value: inv.tenancyId ? <Link to={`/app/tenancies/${inv.tenancyId}`} className="text-bronze-600 hover:underline">{inv.tenancyReference ?? 'Tenancy'}</Link> : <span className="text-ink-400">—</span> },
            { label: 'Property', value: [inv.propertyName, inv.unitReference].filter(Boolean).join(' · ') || <span className="text-ink-400">—</span> },
            { label: 'Company', value: inv.entityName ?? '—' },
          ]} />
        </div>
        <Card title="Status">
          <div className="space-y-3 text-[13px]">
            <div className="flex flex-wrap items-center gap-1.5"><Badge value={inv.status} />{inv.isOverdue && <Badge value="OVERDUE" />}<span className="text-ink-500">{typeLabel(inv.invoiceType)}</span></div>
            <p className="text-[12px] text-ink-500">{inv.journalId ? <>Posted to the ledger · {journalLink(inv.journalId, 'view journal')}</> : 'Not posted to the ledger.'}</p>
            {latest && <p className="text-[12px] text-ink-500">Payment method: <span className="text-ink-800">{methodLabel(latest.method)}</span></p>}
            {onPay && <Button className="w-full" icon="Banknote" onClick={onPay}>Record payment</Button>}
          </div>
        </Card>
      </div>

      <Card title="Lines" actions={<IncVatChip on={inv.pricesIncludeTax} />}>
        <div className="-mx-4 -my-4 overflow-x-auto pb-4">
          <table className="w-full min-w-[760px] text-[13px]">
            <thead><tr className="border-b border-ink-100 bg-ink-50/60 text-left">
              <th className={`${TH} pl-5`}>Description</th>
              <th className={`${TH} text-right`}>Qty</th>
              <th className={`${TH} text-right`}>{inv.pricesIncludeTax ? 'Unit price inc. VAT' : 'Unit price (net)'}</th>
              <th className={TH}>VAT rate</th>
              <th className={`${TH} text-right`}>Net</th>
              <th className={`${TH} text-right`}>VAT</th>
              <th className={`${TH} pr-5 text-right`}>Gross</th>
            </tr></thead>
            <tbody>{lines.map((l) => (
              <tr key={l.id} className="border-b border-ink-100 align-top">
                <td className="px-5 py-2.5 text-ink-700">{l.description}{l.itemCode && <span className="block font-mono text-[10.5px] text-ink-400">{l.itemCode}</span>}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">{l.quantity}</td>
                <td className="px-3 py-2.5 text-right"><Money exact minor={l.unitPriceMinor} currency={c} /></td>
                <td className="px-3 py-2.5 text-ink-600"><span className="font-mono text-[11.5px]">{l.taxCode}</span> {pct(l.taxRate)}</td>
                <td className="px-3 py-2.5 text-right"><Money exact minor={l.netMinor} currency={c} /></td>
                <td className="px-3 py-2.5 text-right text-ink-600"><Money exact minor={l.taxMinor} currency={c} /></td>
                <td className="px-5 py-2.5 text-right"><Money exact minor={l.grossMinor} currency={c} /></td>
              </tr>))}
              {lines.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-ink-400">No lines.</td></tr>}
            </tbody>
            <tfoot>
              <TotalRow span={6} label="Subtotal (net)" minor={inv.subtotalMinor} currency={c} />
              <TotalRow span={6} label="VAT" minor={inv.taxMinor} currency={c} />
              <TotalRow span={6} label="Total" minor={inv.totalMinor} currency={c} strong />
            </tfoot>
          </table>
          <VatSummaryTable lines={lines} currency={c} />
        </div>
      </Card>

      <Card title="Ledger" subtitle="Receipts recorded against this invoice">
        {!canReceipts ? <p className="py-4 text-center text-[12.5px] text-ink-400">You cannot see payments.</p>
          : receiptsError ? <p className="py-4 text-center text-[12.5px] text-red-600">{receiptsError.message}</p>
          : !receipts?.length ? <p className="py-4 text-center text-[12.5px] text-ink-400">{inv.status === 'DRAFT' ? 'Nothing is posted until the invoice is issued.' : 'No receipts yet.'}</p>
          : (
            <div className="-mx-4 -my-4 overflow-x-auto">
              <table className="w-full min-w-[600px] text-[13px]">
                <thead><tr className="border-b border-ink-100 bg-ink-50/60 text-left">
                  <th className={`${TH} pl-5`}>Date</th><th className={TH}>Type</th><th className={TH}>Reference</th><th className={TH}>Method</th>
                  <th className={`${TH} text-right`}>Amount</th><th className={`${TH} pr-5`}>Status</th>
                </tr></thead>
                <tbody>{receipts.map((p) => (
                  <tr key={p.id} className="border-b border-ink-100 last:border-0">
                    <td className="whitespace-nowrap px-5 py-2.5">{p.receivedOn ? formatDate(p.receivedOn) : '—'}</td>
                    <td className="px-3 py-2.5">Receipt</td>
                    <td className="px-3 py-2.5 font-mono text-[12px] text-ink-700">{p.reference ?? '—'}{p.externalReference && <span className="block font-sans text-[11px] text-ink-400">{p.externalReference}</span>}</td>
                    <td className="px-3 py-2.5 text-ink-600">{methodLabel(p.method)}</td>
                    <td className="px-3 py-2.5 text-right font-medium"><Money exact minor={p.amountMinor} currency={p.currency} /></td>
                    <td className="px-5 py-2.5"><Badge value={p.status} /></td>
                  </tr>))}
                </tbody>
              </table>
            </div>
          )}
      </Card>

      {inv.notes && <Card title="Notes"><p className="whitespace-pre-wrap text-[13px] text-ink-600">{inv.notes}</p></Card>}
    </div>
  );
}

// ─── payments tab ─────────────────────────────────────────────────────────────

/** A label/value row of the tab forms (the reference layout's two-column table). */
/** One cell of a three-column field grid: label above, value or control below, hint underneath. */
function Row({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="mb-1.5 text-[12.5px] font-bold uppercase tracking-wider text-ink-900">{label}</dt>
      <dd className="min-w-0 text-[13px] text-ink-900">{children}{hint && <p className="mt-1 text-[11px] text-ink-400">{hint}</p>}</dd>
    </div>
  );
}

const newKey = () => (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`);

/**
 * Record a receipt (while the invoice is payable) and the receipts recorded. A CLEARED receipt posts Dr Bank / Cr AR
 * and settles the invoice; a PENDING one books nothing until it is marked cleared. Marking a cleared one failed
 * reverses its posting (payments.service.ts updatePaymentStatus: PENDING → CLEARED | FAILED, CLEARED → FAILED).
 */
function PaymentsTab({ inv, payable, canRecord, canSee, receipts, receiptsError, focusToken, onChanged }: {
  inv: Invoice; payable: boolean; canRecord: boolean; canSee: boolean; receipts: Receipt[] | undefined; receiptsError: Error | null;
  focusToken: number; onChanged: (msg: string) => void;
}) {
  const canUpdatePayments = useCan('payments.update');
  const blank = () => ({ receivedOn: today(), amount: (inv.outstandingMinor / 100).toFixed(2), method: 'BANK_TRANSFER', externalReference: '', status: 'CLEARED' as 'CLEARED' | 'PENDING' });
  const [form, setForm] = useState(blank);
  // One key per attempt: a retry of the same submit (double click, flaky network) cannot record the payment twice.
  const [idemKey, setIdemKey] = useState(newKey);
  const amountRef = useRef<HTMLInputElement>(null);
  useEffect(() => { amountRef.current?.focus(); amountRef.current?.select(); }, [focusToken]);

  const amountMinor = toMinor(form.amount);
  const amountError = amountMinor === null || amountMinor <= 0 ? 'Enter an amount above zero.'
    : amountMinor > inv.outstandingMinor ? `More than the ${formatMoneyExact(inv.outstandingMinor, inv.currency)} outstanding.` : null;

  const pay = useMutation({
    mutationFn: async () => api.post('/payments', {
      invoiceId: inv.id, amountMinor, receivedOn: form.receivedOn, method: form.method,
      externalReference: form.externalReference.trim() || null, status: form.status,
    }, { 'Idempotency-Key': idemKey }),
    onSuccess: () => {
      const pending = form.status === 'PENDING';
      setIdemKey(newKey());
      setForm(blank());
      onChanged(pending ? 'Payment recorded as pending — nothing is posted until it is marked cleared.' : 'Payment recorded and posted.');
    },
  });

  const [acting, setActing] = useState<{ p: Receipt; to: 'CLEARED' | 'FAILED' } | null>(null);
  const setStatus = useMutation({
    mutationFn: async ({ p, to, reason }: { p: Receipt; to: 'CLEARED' | 'FAILED'; reason: string }) => api.post(`/payments/${p.id}/status`, { status: to, reason: reason || undefined }),
    onSuccess: (_d, v) => {
      setActing(null);
      onChanged(v.to === 'CLEARED' ? 'Payment marked cleared and posted.' : v.p.status === 'CLEARED' ? 'Payment marked failed — its posting is reversed.' : 'Payment marked failed.');
    },
  });
  const c = inv.currency;

  return (
    <div className="space-y-2.5">
      {payable && canRecord && (
        <Card>
          <form className="p-5" onSubmit={(e) => { e.preventDefault(); if (!amountError) pay.mutate(); }}>
            <dl className="grid gap-x-6 gap-y-5 sm:grid-cols-3">
              <Row label="Date received"><Input aria-label="Date received" type="date" required value={form.receivedOn} max={today()} onChange={(e) => setForm({ ...form, receivedOn: e.target.value })} className="w-full" /></Row>
              <Row label={`Amount (${c})`} hint={form.amount && amountError ? <span className="text-red-600">{amountError}</span> : undefined}>
                <Input ref={amountRef} aria-label="Amount" type="text" inputMode="decimal" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} className="w-full" />
              </Row>
              <Row label="Method"><Select aria-label="Method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="w-full">
                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </Select></Row>
              <Row label="Reference">
                <Input aria-label="Reference" maxLength={80} value={form.externalReference} placeholder="e.g. BACS 40-11-22 or cheque 000123" onChange={(e) => setForm({ ...form, externalReference: e.target.value })} className="w-full" />
              </Row>
              <Row label="Status"><Select aria-label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as 'CLEARED' | 'PENDING' })} className="w-full">
                <option value="CLEARED">Cleared — posts to the ledger</option>
                <option value="PENDING">Awaiting confirmation — nothing posted yet</option>
              </Select></Row>
              <div className="flex items-end">
                <Button type="submit" icon="Banknote" className="w-full justify-center" disabled={!!amountError || !form.receivedOn || pay.isPending}>{pay.isPending ? 'Recording…' : 'Record payment'}</Button>
              </div>
            </dl>
            {!!pay.error && <div className="mt-4"><FormError error={pay.error} /></div>}
          </form>
        </Card>
      )}
      {!payable && (inv.status === 'DRAFT' || inv.status === 'VOID') && (
        <p className="rounded-lg bg-ink-50 px-3.5 py-2.5 text-[12.5px] text-ink-500">
          {inv.status === 'DRAFT' ? 'Issue the invoice before recording a payment.' : 'This invoice is void.'}
        </p>
      )}

      <Card>
        {!canSee ? <p className="py-4 text-center text-[12.5px] text-ink-400">You cannot see payments.</p>
          : receiptsError ? <p className="py-4 text-center text-[12.5px] text-red-600">{receiptsError.message}</p>
          : !receipts?.length ? <p className="py-4 text-center text-[12.5px] text-ink-400">No payments recorded yet.</p>
          : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-[13px]">
                <thead><tr className="border-b border-ink-100 bg-ink-50/60 text-left">
                  <th className={`${TH} pl-5`}>Date</th><th className={TH}>Reference</th><th className={TH}>Method</th>
                  <th className={`${TH} text-right`}>Amount</th><th className={TH}>Status</th><th className="w-12 pr-5" />
                </tr></thead>
                <tbody>{receipts.map((p) => {
                  const actions = canUpdatePayments ? [
                    p.status === 'PENDING' && { label: 'Mark cleared', icon: 'CheckCircle2', onClick: () => setActing({ p, to: 'CLEARED' }) },
                    (p.status === 'PENDING' || p.status === 'CLEARED') && { label: 'Mark failed', icon: 'XCircle', danger: true, onClick: () => setActing({ p, to: 'FAILED' }) },
                  ].filter(Boolean) : [];
                  return (
                    <tr key={p.id} className="border-b border-ink-100 last:border-0">
                      <td className="whitespace-nowrap px-5 py-2.5">{p.receivedOn ? formatDate(p.receivedOn) : '—'}</td>
                      <td className="px-3 py-2.5 font-mono text-[12px] text-ink-700">{p.reference ?? '—'}{p.externalReference && <span className="block font-sans text-[11px] text-ink-400">{p.externalReference}</span>}</td>
                      <td className="px-3 py-2.5 text-ink-600">{methodLabel(p.method)}</td>
                      <td className="px-3 py-2.5 text-right font-medium"><Money exact minor={p.amountMinor} currency={p.currency} /></td>
                      <td className="px-3 py-2.5"><Badge value={p.status} /></td>
                      <td className="py-2 pr-5 text-right">{actions.length > 0 && <ActionMenu vertical label={`Actions for ${p.reference ?? 'payment'}`} items={actions} />}</td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </div>
          )}
      </Card>

      {acting && (
        <Confirm open onClose={() => { setActing(null); setStatus.reset(); }} danger={acting.to === 'FAILED'}
          title={acting.to === 'CLEARED' ? `Mark ${acting.p.reference ?? 'this payment'} cleared?` : `Mark ${acting.p.reference ?? 'this payment'} failed?`}
          body={acting.to === 'CLEARED'
            ? `The money has arrived: posts Dr Bank / Cr Accounts Receivable for ${formatMoneyExact(acting.p.amountMinor, acting.p.currency)} and settles the invoice by that much.`
            : acting.p.status === 'CLEARED'
              ? `The money did not stay (bounced cheque, recalled transfer): reverses the posting, and ${formatMoneyExact(acting.p.amountMinor, acting.p.currency)} is owed on the invoice again. This cannot be undone.`
              : 'The money never arrived. Nothing was posted, so nothing is reversed. This cannot be undone.'}
          confirmLabel={acting.to === 'CLEARED' ? 'Mark cleared' : 'Mark failed'}
          reasonLabel={acting.to === 'FAILED' ? 'Reason (required — goes on the audit trail)' : undefined}
          pending={setStatus.isPending} error={setStatus.error} onConfirm={(reason) => setStatus.mutate({ p: acting.p, to: acting.to, reason })} />
      )}
    </div>
  );
}

// ─── details tab ──────────────────────────────────────────────────────────────

/** The invoice's fields. A draft's type, tenancy and dates can be changed here; an issued invoice is read-only. */
function DetailsTab({ inv, canEdit, journalLink, onSaved }: { inv: Invoice; canEdit: boolean; journalLink: (jid: string | null, label: string) => ReactNode; onSaved: (msg: string) => void }) {
  const editable = inv.status === 'DRAFT' && canEdit;
  const [form, setForm] = useState({ invoiceType: (inv.invoiceType === 'CREDIT_NOTE' ? 'RENT' : inv.invoiceType) as InvoiceType, issueDate: inv.issueDate, dueDate: inv.dueDate });
  const [tenancy, setTenancy] = useState<Tenancy | null>(() => (inv.tenancyId ? {
    id: inv.tenancyId, reference: inv.tenancyReference ?? 'Tenancy', propertyId: inv.propertyId, unitId: inv.unitId,
    tenants: inv.customerUserId ? [{ id: inv.customerUserId, name: inv.customerName ?? '' }] : [],
    property: inv.propertyName ? { name: inv.propertyName } : undefined, unit: inv.unitReference ? { reference: inv.unitReference } : undefined,
  } : null));
  const [tenancyChanged, setTenancyChanged] = useState(false);
  const searchTenancies = async (q: string) => (await api.get<Tenancy[]>('/tenancies', { search: q || undefined, perPage: 20 })).data;

  const save = useMutation({
    mutationFn: async () => api.patch(`/invoices/${inv.id}`, {
      invoiceType: form.invoiceType, issueDate: form.issueDate, dueDate: form.dueDate,
      // The tenancy carries the customer, property and unit; untouched, they stay as saved.
      ...(tenancyChanged ? { tenancyId: tenancy?.id ?? null, customerUserId: tenancy?.tenants?.[0]?.id ?? null, propertyId: tenancy?.propertyId ?? null, unitId: tenancy?.unitId ?? null } : {}),
    }),
    onSuccess: () => onSaved('Draft saved.'),
  });
  const dirty = tenancyChanged || form.invoiceType !== inv.invoiceType || form.issueDate !== inv.issueDate || form.dueDate !== inv.dueDate;
  const datesOk = !!form.issueDate && !!form.dueDate && form.dueDate >= form.issueDate;

  const customer = tenancyChanged ? tenancy?.tenants?.[0]?.name : inv.customerName;
  const where = tenancyChanged ? [tenancy?.property?.name, tenancy?.unit?.reference] : [inv.propertyName, inv.unitReference];
  const none = <span className="text-ink-400">—</span>;

  return (
    <Card>
      <dl className="grid gap-x-6 gap-y-7 p-5 sm:grid-cols-3">
        <Row label="Company">{inv.entityName ?? '—'}</Row>
        <Row label="Type">{editable
          ? <Select aria-label="Type" value={form.invoiceType} onChange={(e) => setForm({ ...form, invoiceType: e.target.value as InvoiceType })} className="w-full">{TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</Select>
          : typeLabel(inv.invoiceType)}</Row>
        <Row label="Tenancy">{editable
          ? <div><SearchSelect<Tenancy> aria-label="Tenancy" value={tenancy} onChange={(t) => { setTenancy(t); setTenancyChanged(true); }} loadOptions={searchTenancies}
              getOptionKey={(t) => t.id} getOptionLabel={tenancyLabel} placeholder="Search tenancies (optional)" /></div>
          : inv.tenancyId ? <Link to={`/app/tenancies/${inv.tenancyId}`} className="text-bronze-600 hover:underline">{inv.tenancyReference ?? 'Tenancy'}</Link> : none}</Row>
        <Row label="Customer">{customer || none}</Row>
        <Row label="Property · unit">{where.filter(Boolean).join(' · ') || none}</Row>
        <Row label="Issue date">{editable
          ? <Input aria-label="Issue date" type="date" value={form.issueDate} onChange={(e) => setForm({ ...form, issueDate: e.target.value })} className="w-full" />
          : <DateText value={inv.issueDate} />}</Row>
        <Row label="Due date" hint={editable && !datesOk && form.dueDate ? <span className="text-red-600">The due date can't be before the issue date.</span> : undefined}>{editable
          ? <Input aria-label="Due date" type="date" value={form.dueDate} min={form.issueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} className="w-full" />
          : <span className={inv.isOverdue ? 'font-medium text-red-600' : ''}><DateText value={inv.dueDate} /></span>}</Row>
        <Row label="Invoice #"><span className="font-mono text-[12.5px]">{invoiceNumberLabel(inv.number)}</span>{inv.status === 'DRAFT' && <span className="ml-1.5 text-[11px] text-ink-400">(numbered on issue)</span>}</Row>
        <Row label="Status" hint="Updates automatically from payments and voids."><span className="inline-flex gap-1.5"><Badge value={inv.status} />{inv.isOverdue && <Badge value="OVERDUE" />}</span></Row>
        <Row label="Currency">{inv.currency}</Row>
        <Row label="Prices">{inv.pricesIncludeTax ? 'Include VAT' : 'Net of VAT'}</Row>
        {inv.journalId && <Row label="Journal">{journalLink(inv.journalId, 'View the posting')}</Row>}
      </dl>
      {editable && (
        <div className="flex items-center justify-end gap-3 border-t border-ink-100 px-5 py-3">
          <div className="flex-1"><FormError error={save.error} /></div>
          <Button disabled={!dirty || !datesOk || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save changes'}</Button>
        </div>
      )}
    </Card>
  );
}

// ─── notes tab ────────────────────────────────────────────────────────────────

/** Notes are printed on the invoice. They stay editable after issue (they move no money); a void invoice is final. */
function NotesTab({ inv, canEdit, onSaved }: { inv: Invoice; canEdit: boolean; onSaved: (msg: string) => void }) {
  const [notes, setNotes] = useState(inv.notes ?? '');
  const editable = canEdit && inv.status !== 'VOID';
  const save = useMutation({
    mutationFn: async () => api.patch(`/invoices/${inv.id}`, { notes: notes.trim() ? notes : null }),
    onSuccess: () => onSaved('Notes saved.'),
  });
  const dirty = (notes.trim() ? notes : null) !== (inv.notes ?? null);
  return (
    <Card title="Notes" subtitle="Printed on the invoice document">
      {editable ? (
        <div className="space-y-3">
          <Textarea aria-label="Notes" rows={6} maxLength={4000} value={notes} placeholder="e.g. payment instructions, or what this invoice covers" onChange={(e) => setNotes(e.target.value)} className="w-full" />
          <div className="flex items-center justify-end gap-3">
            <div className="flex-1"><FormError error={save.error} /></div>
            <Button disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? 'Saving…' : 'Save notes'}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {inv.notes ? <p className="whitespace-pre-wrap text-[13px] text-ink-700">{inv.notes}</p> : <p className="text-[13px] text-ink-400">No notes.</p>}
          <p className="text-[11.5px] text-ink-400">{inv.status === 'VOID' ? 'A void invoice cannot be changed.' : 'You cannot edit invoices.'}</p>
        </div>
      )}
    </Card>
  );
}

// ─── edit mode (new, or a draft) ──────────────────────────────────────────────

/**
 * One line being edited. descriptionTouched: typed by hand, so picking an item never overwrites it. rateTouched: chosen
 * by hand, so the line stops following the invoice type's default. Picking an item always re-fills price and rate.
 */
interface Line {
  key: number;
  item: Item | null;
  description: string;
  descriptionTouched: boolean;
  quantity: string;
  /** Major units, VAT included. */
  unitPrice: string;
  /** '' = follow the invoice type's default code (a line with no item whose rate nobody chose). */
  taxCode: string;
  rateTouched: boolean;
}

let nextKey = 1;
const blankLine = (): Line => ({ key: nextKey++, item: null, description: '', descriptionTouched: false, quantity: '1', unitPrice: '', taxCode: '', rateTouched: false });

/** A saved draft line as an editable one. Old net-priced drafts are shown at their gross unit price. */
function lineFromSaved(l: InvoiceLine, inclusive: boolean, currency: string, region: 'UK' | 'AE'): Line {
  const unitGross = inclusive ? l.unitPriceMinor : Math.round(l.grossMinor / (l.quantity || 1));
  // The line's item, enough of it for the picker to show and keep (the price and rate are on the line already).
  const item = l.itemId ? ({ id: l.itemId, code: l.itemCode ?? '', name: l.description, unitPriceMinor: 0, currency, pricesIncludeTax: true, taxCode: l.taxCode, taxRatePercent: l.taxRate, region } as Item) : null;
  return { key: nextKey++, item, description: l.description, descriptionTouched: true, quantity: String(l.quantity), unitPrice: (unitGross / 100).toFixed(2), taxCode: l.taxCode, rateTouched: true };
}

/** Major-unit text → minor units, or null when it is not a number. */
const toMinor = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n * 100) : null;
};
const qtyOf = (l: Line) => { const n = Number(l.quantity); return Number.isFinite(n) && n > 0 ? n : 1; };

/** An item's price with VAT included, whatever basis the item stores it on. */
function itemGrossMinor(item: Item): number {
  if (item.pricesIncludeTax) return item.unitPriceMinor;
  return item.unitPriceMinor + Math.round((item.unitPriceMinor * (item.taxRatePercent ?? 0)) / 100);
}

const lineReady = (l: Line) => (!!l.item || l.description.trim() !== '') && (toMinor(l.unitPrice) !== null || !!l.item);

// "Standard rate (20%)" already carries its percentage, so the option reads "Standard rate · 20%" and fits the select.
const rateOptionLabel = (label: string, ratePercent: number) => `${label.replace(/\s*\([^)]*%\)\s*$/, '')} · ${pct(ratePercent)}`;

const tenancyLabel = (t: Tenancy) => [t.reference, t.tenants?.[0]?.name, [t.property?.name, t.unit?.reference].filter(Boolean).join(' ')].filter(Boolean).join(' · ');

function InvoiceEditor({ inv, onCreated, onSaved }: { inv: Invoice | null; onCreated: (inv: Invoice) => void; onSaved: (msg: string) => void }) {
  const [form, setForm] = useState({
    entityId: inv?.entityId ?? '', invoiceType: ((inv?.invoiceType === 'CREDIT_NOTE' ? 'RENT' : inv?.invoiceType) ?? 'RENT') as InvoiceType,
    issueDate: inv?.issueDate ?? today(), dueDate: inv?.dueDate ?? today(), notes: inv?.notes ?? '',
  });
  // Who and where: a tenancy fills the customer, property and unit.
  const [tenancy, setTenancy] = useState<Tenancy | null>(() => (inv?.tenancyId ? {
    id: inv.tenancyId, reference: inv.tenancyReference ?? 'Tenancy', propertyId: inv.propertyId, unitId: inv.unitId,
    tenants: inv.customerUserId ? [{ id: inv.customerUserId, name: inv.customerName ?? '' }] : [],
    property: inv.propertyName ? { name: inv.propertyName } : undefined, unit: inv.unitReference ? { reference: inv.unitReference } : undefined,
  } : null));
  const [tenancyChanged, setTenancyChanged] = useState(false);

  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<Entity[]>('/entities')).data, enabled: !inv });
  // Until one is chosen, the first company in the list. A saved draft keeps its company.
  const entityId = inv?.entityId ?? (form.entityId || entities.data?.[0]?.id || '');
  const entity = entities.data?.find((e) => e.id === entityId);
  const region = inv?.region ?? entity?.region ?? 'UK';
  const currency = inv?.currency ?? entity?.functionalCurrency ?? 'GBP';
  const [lines, setLines] = useState<Line[]>(() => (inv?.lines?.length ? inv.lines.map((l) => lineFromSaved(l, inv.pricesIncludeTax, inv.currency, inv.region)) : [blankLine()]));

  const rates = useQuery({ queryKey: ['/tax-rates', region, false], queryFn: async () => (await api.get<TaxRate[]>('/tax-rates', { region })).data });
  const defaults = useQuery({ queryKey: ['/tax-rates/defaults', region], queryFn: async () => (await api.get<TaxDefaults>('/tax-rates/defaults', { region })).data });
  // The company's payment terms: the due date follows issue date + terms until it is set by hand (a saved draft keeps its own).
  const invoicing = useQuery({ queryKey: ['/entities', entityId, 'invoicing'], queryFn: async () => (await api.get<InvoicingSettings>(`/entities/${entityId}/invoicing`)).data, enabled: !!entityId, retry: false });
  const termsDays = invoicing.data?.paymentTermsDays;
  const [dueTouched, setDueTouched] = useState(!!inv);
  const dueDate = !dueTouched && termsDays !== undefined ? addDays(form.issueDate, termsDays) : !dueTouched ? addDays(form.issueDate, 14) : form.dueDate;

  // The VAT rates in force today, one per code.
  const inForce = useMemo(() => {
    const byCode = new Map<string, TaxRate>();
    for (const r of rates.data ?? []) if (r.inForce && !byCode.has(r.code)) byCode.set(r.code, r);
    return [...byCode.values()].sort((a, b) => b.ratePercent - a.ratePercent || a.code.localeCompare(b.code));
  }, [rates.data]);
  const rateOf = (code: string) => inForce.find((r) => r.code === code);
  const defaultCode = defaults.data?.mappings.find((m) => m.invoiceType === form.invoiceType)?.code ?? defaults.data?.fallbackCode ?? '';
  const codeOf = (l: Line) => l.taxCode || defaultCode;

  const setLine = (key: number, patch: Partial<Line>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  /** Picking an item fills its description (unless typed by hand), its price (when it has one: system items are 0) and its VAT code. */
  const pickItem = (key: number, item: Item | null) => setLines((ls) => ls.map((l) => {
    if (l.key !== key) return l;
    if (!item) return { ...l, item: null, taxCode: l.rateTouched ? l.taxCode : '' };
    return {
      ...l, item,
      description: l.descriptionTouched ? l.description : item.name,
      unitPrice: item.unitPriceMinor > 0 ? (itemGrossMinor(item) / 100).toFixed(2) : l.unitPrice,
      taxCode: item.taxCode, rateTouched: false,
    };
  }));

  const party = tenancy
    ? { tenancyId: tenancy.id, customerUserId: tenancy.tenants?.[0]?.id ?? null, propertyId: tenancy.propertyId ?? null, unitId: tenancy.unitId ?? null }
    : { tenancyId: null, customerUserId: tenancyChanged ? null : inv?.customerUserId ?? null, propertyId: tenancyChanged ? null : inv?.propertyId ?? null, unitId: tenancyChanged ? null : inv?.unitId ?? null };
  const linesBody = () => lines.filter(lineReady).map((l) => {
    const price = toMinor(l.unitPrice);
    return { itemId: l.item?.id ?? undefined, description: l.description.trim() || undefined, quantity: qtyOf(l), unitPriceMinor: price ?? undefined, taxCode: codeOf(l) || undefined };
  });
  const body = (): CreateInvoiceBody => ({
    entityId, invoiceType: form.invoiceType, tenancyId: party.tenancyId ?? undefined,
    customerUserId: party.customerUserId ?? undefined, propertyId: party.propertyId ?? undefined, unitId: party.unitId ?? undefined,
    issueDate: form.issueDate, dueDate, notes: form.notes || undefined, pricesIncludeTax: true, lines: linesBody(),
  });
  const ready = !!entityId && lines.some(lineReady) && lines.every((l) => l.unitPrice.trim() === '' || (toMinor(l.unitPrice) ?? -1) >= 0);

  // Server-exact totals: the preview of the same body, debounced.
  const bodyKey = JSON.stringify(ready ? body() : null);
  const [debouncedKey, setDebouncedKey] = useState(bodyKey);
  useEffect(() => { const t = setTimeout(() => setDebouncedKey(bodyKey), 300); return () => clearTimeout(t); }, [bodyKey]);
  const preview = useQuery({
    queryKey: ['invoice-preview', debouncedKey],
    queryFn: async () => (await api.post<InvoicePreview>('/invoices/preview', JSON.parse(debouncedKey))).data,
    enabled: debouncedKey !== 'null',
    retry: false,
    staleTime: 60_000,
  });
  const exact = !!preview.data && !preview.isFetching && debouncedKey === bodyKey;

  // Local estimate while the preview is on its way (the server's figures replace it).
  const local = useMemo(() => {
    let net = 0, tax = 0, total = 0;
    for (const l of lines.filter(lineReady)) {
      const price = toMinor(l.unitPrice) ?? (l.item ? itemGrossMinor(l.item) : 0);
      const gross = Math.round(price * qtyOf(l));
      const def = rateOf(codeOf(l));
      const rate = def?.taxable === false ? 0 : (def?.ratePercent ?? 0);
      const n = Math.round((gross * 100) / (100 + rate));
      net += n; tax += gross - n; total += gross;
    }
    return { subtotalMinor: net, taxMinor: tax, totalMinor: total };
  }, [lines, inForce, defaultCode]); // eslint-disable-line react-hooks/exhaustive-deps
  const totals = exact ? preview.data! : local;

  // Per line: the preview's figures when current, else qty × price for the gross.
  const readyKeys = lines.filter(lineReady).map((l) => l.key);
  const previewLine = (l: Line) => { const i = readyKeys.indexOf(l.key); return exact && i >= 0 ? preview.data!.lines[i] ?? null : null; };
  const lineGross = (l: Line) => {
    const p = previewLine(l);
    if (p) return p.grossMinor;
    const price = toMinor(l.unitPrice) ?? (l.item ? itemGrossMinor(l.item) : null);
    return price === null ? null : Math.round(price * qtyOf(l));
  };

  const searchItems = async (q: string) => (await api.get<Item[]>('/items', { q, region, entityId: entityId || undefined, invoiceType: form.invoiceType, perPage: 20 })).data;
  const searchTenancies = async (q: string) => (await api.get<Tenancy[]>('/tenancies', { search: q || undefined, perPage: 20 })).data;

  const save = useMutation({
    mutationFn: async (andIssue: boolean) => {
      let saved: Invoice;
      if (!inv) saved = (await api.post<Invoice>('/invoices', body())).data;
      else {
        saved = (await api.patch<Invoice>(`/invoices/${inv.id}`, {
          invoiceType: form.invoiceType, tenancyId: party.tenancyId, customerUserId: party.customerUserId, propertyId: party.propertyId, unitId: party.unitId,
          issueDate: form.issueDate, dueDate, notes: form.notes.trim() ? form.notes : null, pricesIncludeTax: true, lines: linesBody(),
        })).data;
      }
      if (andIssue) await api.post(`/invoices/${saved.id}/issue`, {});
      return { saved, andIssue };
    },
    onSuccess: ({ saved, andIssue }) => {
      if (!inv) onCreated(saved);
      else onSaved(andIssue ? 'Invoice issued and posted to the ledger.' : 'Draft saved.');
    },
  });

  const rateOptions = (code: string) => (
    <>
      {!code && <option value="">Choose…</option>}
      {inForce.map((r) => <option key={r.code} value={r.code}>{rateOptionLabel(r.label, r.ratePercent)}</option>)}
      {code && !rateOf(code) && <option value={code}>{code}</option>}
    </>
  );

  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const addLine = () => {
    setLines((ls) => [...ls, blankLine()]);
    requestAnimationFrame(() => tbodyRef.current?.querySelector<HTMLInputElement>('tr:last-child input[role="combobox"]')?.focus());
  };
  const field = 'w-full';

  return (
    <div className="space-y-2.5">
      <div className="grid items-start gap-4 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <MetaCard draft currency={currency} balanceMinor={totals.totalMinor} totalMinor={totals.totalMinor} paidMinor={0} rows={[
            { label: 'Company', value: inv ? <span>{inv.entityName ?? '—'}</span>
              : <Select aria-label="Company" value={entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })} className={field}>{entities.data?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select> },
            { label: 'Type', value: <Select aria-label="Type" title="Sets the default VAT rate" value={form.invoiceType} onChange={(e) => setForm({ ...form, invoiceType: e.target.value as InvoiceType })} className={field}>{TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}</Select> },
            { label: 'Tenancy', value: <SearchSelect<Tenancy> aria-label="Tenancy" value={tenancy} onChange={(t) => { setTenancy(t); setTenancyChanged(true); }} loadOptions={searchTenancies}
              getOptionKey={(t) => t.id} getOptionLabel={tenancyLabel} placeholder="Search tenancies (optional)"
              renderOption={(t) => <div className="flex items-baseline justify-between gap-3"><span className="min-w-0 truncate">{tenancyLabel(t)}</span>{t.status && <span className="shrink-0 text-[11px] text-ink-400">{pretty(t.status)}</span>}</div>} /> },
            { label: 'Billed to', value: <span className="font-semibold">{tenancy?.tenants?.[0]?.name || (!tenancyChanged && inv?.customerName) || <span className="font-normal text-ink-400">Pick a tenancy</span>}</span> },
            ...(inv ? [{ label: 'Invoice #', value: <span className="font-mono text-[12.5px] text-ink-500">{invoiceNumberLabel(inv.number)} <span className="font-sans text-[11px] text-ink-400">(numbered on issue)</span></span> }] : []),
            { label: 'Issue date', value: <Input aria-label="Issue date" type="date" value={form.issueDate} onChange={(e) => setForm({ ...form, issueDate: e.target.value })} className={field} /> },
            { label: 'Due date', value: <div><Input aria-label="Due date" type="date" value={dueDate} onChange={(e) => { setDueTouched(true); setForm({ ...form, dueDate: e.target.value }); }} className={field} />
              {!dueTouched && invoicing.data && <p className="mt-0.5 text-[11px] text-ink-400">Company terms: {invoicing.data.termsText}</p>}</div> },
          ]} />
        </div>
        <Card title="Status">
          <div className="space-y-3 text-[13px]">
            <div className="flex items-center gap-1.5"><Badge value="DRAFT" /><span className="text-ink-500">{inv ? 'Not issued yet' : 'Not saved yet'}</span></div>
            <p className="text-[12px] text-ink-500">Issuing allocates the invoice number and posts it to the ledger. After that it can only be voided.</p>
            <div className="flex gap-2">
              <Button variant="secondary" className="flex-1" disabled={!ready || save.isPending} onClick={() => save.mutate(false)}>{save.isPending && !save.variables ? 'Saving…' : 'Save as draft'}</Button>
              <Can do="invoices.update"><Button className="flex-1" disabled={!ready || save.isPending} onClick={() => save.mutate(true)}>{save.isPending && save.variables ? 'Issuing…' : 'Save & issue'}</Button></Can>
            </div>
            <FormError error={save.error} />
          </div>
        </Card>
      </div>

      <Card title="Lines" actions={<div className="flex items-center gap-3"><IncVatChip on /><button type="button" onClick={addLine} className="inline-flex items-center gap-1 text-[12px] text-bronze-600 hover:underline"><Plus className="h-3.5 w-3.5" /> Add line</button></div>}>
        <div className="-mx-4 -my-4 overflow-x-auto pb-4">
          <table className="w-full min-w-[980px] text-[13px]">
            <thead><tr className="border-b border-ink-100 bg-ink-50/60 text-left">
              <th className={`${TH} w-[210px] pl-5`}>Item</th>
              <th className={TH}>Description</th>
              <th className={`${TH} w-[76px] text-right`}>Qty</th>
              <th className={`${TH} w-[120px] whitespace-nowrap text-right`}>Price inc. VAT</th>
              <th className={`${TH} w-[230px]`}>VAT rate</th>
              <th className={`${TH} w-[110px] text-right`}>Gross</th>
              <th className="w-10 pr-5" />
            </tr></thead>
            <tbody ref={tbodyRef}>{lines.map((l, i) => {
              const gross = lineGross(l);
              const p = previewLine(l);
              return (
                <tr key={l.key} className="border-b border-ink-100 align-top">
                  <td className="py-2 pl-5 pr-2">
                    <SearchSelect<Item>
                      aria-label={`Line ${i + 1} item`}
                      value={l.item}
                      onChange={(item) => pickItem(l.key, item)}
                      loadOptions={searchItems}
                      reloadKey={`${entityId}|${form.invoiceType}`}
                      getOptionKey={(o) => o.id}
                      getOptionLabel={(o) => `${o.code} · ${o.name}`}
                      renderOption={(o) => (
                        <div className="flex items-baseline justify-between gap-3">
                          <div className="min-w-0"><span className="font-mono text-[11.5px] text-ink-500">{o.code}</span> <span className="text-ink-900">{o.name}</span></div>
                          <div className="shrink-0 text-right text-[12px] tabular-nums text-ink-600">
                            {o.unitPriceMinor > 0 ? formatMoneyExact(itemGrossMinor(o), o.currency) : <span className="text-ink-300">no price</span>}
                            <span className="ml-1.5 text-ink-400">{o.taxRatePercent === null ? o.taxCode : pct(o.taxRatePercent)}</span>
                          </div>
                        </div>
                      )}
                      placeholder="Search items"
                      allowCustom
                      onCustom={(text) => { if (!l.descriptionTouched || !l.description.trim()) setLine(l.key, { description: text, descriptionTouched: true }); }}
                    />
                  </td>
                  <td className="px-2 py-2"><Input aria-label={`Line ${i + 1} description`} value={l.description} placeholder="Description"
                    onChange={(e) => setLine(l.key, { description: e.target.value, descriptionTouched: true })} className="w-full" /></td>
                  <td className="px-2 py-2"><Input aria-label={`Line ${i + 1} quantity`} type="number" min="0" step="0.5" value={l.quantity}
                    onChange={(e) => setLine(l.key, { quantity: e.target.value })} className="w-full text-right" /></td>
                  <td className="px-2 py-2"><Input aria-label={`Line ${i + 1} unit price including VAT`} type="number" min="0" step="0.01" value={l.unitPrice}
                    placeholder={l.item && l.item.unitPriceMinor > 0 ? (itemGrossMinor(l.item) / 100).toFixed(2) : '0.00'}
                    onChange={(e) => setLine(l.key, { unitPrice: e.target.value })} className="w-full text-right" /></td>
                  <td className="px-2 py-2"><Select aria-label={`Line ${i + 1} VAT rate`} value={codeOf(l)} onChange={(e) => setLine(l.key, { taxCode: e.target.value, rateTouched: true })} className="w-full">
                    {rateOptions(codeOf(l))}
                  </Select></td>
                  <td className="px-2 py-2 pt-4 text-right tabular-nums text-ink-800" aria-label={`Line ${i + 1} total`}>
                    {gross === null ? <span className="text-ink-300">—</span> : formatMoneyExact(gross, currency)}
                    {p && <span className="block text-[10.5px] text-ink-400">VAT {formatMoneyExact(p.taxMinor, currency)}</span>}
                  </td>
                  <td className="py-2 pr-5 pt-3.5 text-right">
                    <button type="button" aria-label={`Remove line ${i + 1}`} onClick={() => setLines(lines.length > 1 ? lines.filter((x) => x.key !== l.key) : [blankLine()])}
                      className="rounded p-1 text-ink-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              );
            })}</tbody>
            <tfoot aria-live="polite">
              <TotalRow span={5} label="Subtotal (net)" minor={totals.subtotalMinor} currency={currency} />
              <TotalRow span={5} label="VAT" minor={totals.taxMinor} currency={currency} />
              <TotalRow span={5} label="Total" minor={totals.totalMinor} currency={currency} strong />
              <tr><td colSpan={7} className="px-5 pt-1 text-right text-[11px]">
                {!exact && ready && !preview.isError && <span className="text-ink-400">Calculating…</span>}
                {preview.isError && debouncedKey === bodyKey && <span className="text-red-600">{(preview.error as Error).message}</span>}
              </td></tr>
            </tfoot>
          </table>
          {exact && <VatSummaryTable lines={preview.data!.lines} currency={currency} />}
        </div>
      </Card>

      <Card title="Notes">
        <Textarea aria-label="Notes" rows={3} value={form.notes} placeholder="Printed on the invoice (optional)" onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full" />
      </Card>
    </div>
  );
}
